'use client';

import classNames from 'classnames/bind';
import EditorViewer from 'ui/common/editorViewer/EditorViewer';
import { COLORS } from '../../../constants/colors';
import { VIEW_MODE, ViewModeType } from '../../../interface/common/ViewMode';
import { IGetJDViewSettingResponse } from '../../../interface/position/getJDViewSetting';
import { IJD } from '../../../interface/recruit/IJD';
import Divider from '../../common/Divider';
import AttachedFileList from '../../file/AttachedFileList';
import style from './JDContent.module.scss';
import JDProcess from './JDProcess';
import useParseJobDescription from './useParseJobDescription';

const cx = classNames.bind(style);

interface IProps {
  params: {
    id: number;
  };
  data?: IJD;
  viewSettingData?: IGetJDViewSettingResponse;
  viewMode?: ViewModeType;
  handleClickApply?: () => void;
  readOnly?: boolean;
}

const initialData: IJD = {
  title: '',
  jobDescription: '',
  jobDescriptionType: 'HTML',
  jobGroup: '',
  careerType: 'NEW',
  address: '',
  detailAddress: '',
  region: '',
  announcementType: 'NORMAL',
  startDateTime: '',
  endDateTime: '',
  tagList: [],
  dday: 0,
  classificationCode: '',
  recruitmentType: 'GENERAL',
  submissionStatus: 'PRE_SUBMISSION',
  attachFileDtoList: [],
  applyUrl: '',
  myPageUrl: '',
  resumeUpdateUrl: '',
};

const ValidatedEditorViewer = ({ data }: { data: string }) => {
  try {
    const value = JSON.parse(data);
    return <>{<EditorViewer value={value} />}</>;
  } catch (e) {
    return (
      <div className={cx('innerHTML')}>
        <div dangerouslySetInnerHTML={{ __html: data }} />
      </div>
    );
  }
};

const JDContent = ({
  params,
  data,
  viewSettingData,
  viewMode = VIEW_MODE.PC,
  handleClickApply,
  readOnly = false,
}: IProps) => {
  const validJDData = data ?? initialData;
  const { title, jobDescription, jobDescriptionType, attachFileDtoList: fileList = [] } = validJDData;
  const { imageOptimizedJobDescription } = useParseJobDescription({ jobDescription });

  const getJDContent = () => {
    return (
      <div className={cx('jobDetail')}>
        {jobDescriptionType === 'JSON' ? (
          <ValidatedEditorViewer data={imageOptimizedJobDescription} />
        ) : (
          <div className={cx('innerHTML')}>
            <div dangerouslySetInnerHTML={{ __html: imageOptimizedJobDescription }} />
          </div>
        )}
      </div>
    );
  };

  const subComponent: Record<string, React.ReactNode> = {
    TITLE: (
      <div className={cx('jobTitle')}>
        <h2>{title}</h2>
        <Divider color={COLORS.gray400} />
      </div>
    ),
    JD_CONTENT: getJDContent(),
    JD_PROCESS: (
      <div className={cx('jobProcess')}>
        <JDProcess
          id={params.id}
          data={validJDData}
          viewSettingData={viewSettingData!}
          handleClickApply={handleClickApply}
          readOnly={readOnly}
        />
      </div>
    ),
    JD_FILE: (
      <AttachedFileList
        className={cx('attachedFileList')}
        fileList={fileList}
        viewMode={viewMode}
        readonly={readOnly}
      />
    ),
  };

  return (
    <div className={cx('container', viewMode)}>
      {viewMode === 'MOBILE' ? (
        <>
          {subComponent.TITLE}
          {subComponent.JD_PROCESS}
          {subComponent.JD_CONTENT}
          {subComponent.JD_FILE}
        </>
      ) : (
        <>
          <div className={cx('wrapper')}>
            {subComponent.TITLE}
            {subComponent.JD_CONTENT}
            {subComponent.JD_FILE}
          </div>
          {subComponent.JD_PROCESS}
        </>
      )}
    </div>
  );
};

export default JDContent;
